import { normalizeProductPricesForDisplay } from './productPricing';
import { getProductStorefrontPath } from './productSeoUrl';

const SHARE_STRIP_QUERY = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'ref',
];

function normalizeShareOrigin(origin) {
  if (!origin) return origin;
  let o = String(origin).trim().replace(/\/+$/, '');
  o = o.replace(/^http:/i, 'https:');
  if (/^https:\/\/superwear\.net$/i.test(o)) {
    o = 'https://www.superwear.net';
  }
  return o;
}

/** Remove tracking params / hash so Facebook scrapes the same URL as a pasted link. */
export function cleanProductShareUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    u.protocol = 'https:';
    u.hash = '';
    SHARE_STRIP_QUERY.forEach((p) => u.searchParams.delete(p));
    if (!u.searchParams.toString()) u.search = '';
    if (u.hostname === 'superwear.net') u.hostname = 'www.superwear.net';
    const out = u.toString();
    return out.endsWith('/') && u.pathname !== '/' ? out.replace(/\/$/, '') : out;
  } catch {
    return url;
  }
}

/**
 * Canonical product URL for share links (WhatsApp, Facebook, copy).
 * Uses REACT_APP_FRONTEND_URL in production so OG scrape matches pasted links.
 */
export function getProductSharePageUrl(product, overrideUrl = '') {
  if (overrideUrl) return cleanProductShareUrl(overrideUrl);

  const path = getProductStorefrontPath(product);
  const normalizedPath =
    path.startsWith('http') ? (() => { try { return new URL(path).pathname; } catch { return ''; } })() : path;

  if (!normalizedPath || normalizedPath === '/') {
    if (typeof window !== 'undefined') {
      return cleanProductShareUrl(window.location.href);
    }
    return '';
  }

  let origin =
    (process.env.REACT_APP_FRONTEND_URL && String(process.env.REACT_APP_FRONTEND_URL).trim()) ||
    (typeof window !== 'undefined' ? window.location.origin : '');

  origin = normalizeShareOrigin(origin);
  return `${origin}${normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`}`;
}

/**
 * Facebook Share Dialog URL — policy-safe: only `href` (no prefilled post text).
 * Title, price, and image come from og:* tags on the shared link.
 */
export function buildFacebookShareUrl(pageUrl) {
  const href = encodeURIComponent(cleanProductShareUrl(pageUrl));
  const appId = (process.env.REACT_APP_FACEBOOK_APP_ID || '').trim();
  if (appId) {
    return `https://www.facebook.com/dialog/share?app_id=${encodeURIComponent(appId)}&href=${href}&display=popup`;
  }
  return `https://www.facebook.com/sharer/sharer.php?u=${href}&display=popup`;
}

/** Plain text for Facebook paste (no WhatsApp *bold* markers, no URL — link comes from share dialog). */
export function buildProductSharePlainText(product) {
  return buildProductShareText(product).replace(/\*([^*]*)\*/g, '$1');
}

export function getFacebookShareClipboardText(product) {
  return buildProductSharePlainText(product);
}

async function copyTextToClipboard(copyText) {
  if (!copyText) return false;

  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(copyText);
      return true;
    } catch {
      /* fall through */
    }
  }

  if (typeof document !== 'undefined') {
    try {
      const ta = document.createElement('textarea');
      ta.value = copyText;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) return true;
    } catch {
      /* fall through */
    }
  }

  return false;
}

/**
 * Open Facebook share and copy product text for paste (Facebook does not allow prefilled post text).
 * @returns {{ opened: boolean, copied: boolean }}
 */
export async function openFacebookProductShare(product, pageUrl = '') {
  const shareUrl = getProductSharePageUrl(product, pageUrl);
  if (!shareUrl || typeof window === 'undefined') {
    return { opened: false, copied: false };
  }

  const copied = await copyTextToClipboard(getFacebookShareClipboardText(product));

  const fbUrl = buildFacebookShareUrl(shareUrl);
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  if (isMobile) {
    window.open(fbUrl, '_blank', 'noopener,noreferrer');
  } else {
    const w = 600;
    const h = 500;
    const left = Math.max(0, (window.screen.width - w) / 2);
    const top = Math.max(0, (window.screen.height - h) / 2);
    window.open(
      fbUrl,
      'facebook-share-dialog',
      `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,status=no,scrollbars=yes`
    );
  }
  return { opened: true, copied };
}

function formatRs(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return '';
  return `Rs.${n.toLocaleString()}`;
}

function normalizeTagLabel(tag) {
  if (tag == null) return '';
  if (typeof tag === 'string') return tag.trim();
  if (typeof tag === 'object') {
    return String(tag.name || tag.tag || tag.value || '').trim();
  }
  return String(tag).trim();
}

function normalizeColorLabel(color) {
  if (color == null) return '';
  if (typeof color === 'string') return color.trim();
  if (typeof color === 'object') {
    return String(color.name || color.value || color.color || '').trim();
  }
  return String(color).trim();
}

function normalizeSizeLabel(size) {
  if (size == null) return '';
  if (typeof size === 'string') return size.trim();
  if (typeof size === 'object') {
    return String(size.name || size.value || size.size || '').trim();
  }
  return String(size).trim();
}

/** Skip placeholder-only SKU (default/default) in share copy. */
function getShareColorLabels(product) {
  if (!product || !Array.isArray(product.colors)) return [];
  const colors = product.colors.map(normalizeColorLabel).filter(Boolean);
  if (!colors.length) return [];

  const sizes = Array.isArray(product.sizes)
    ? product.sizes.map(normalizeSizeLabel).filter(Boolean)
    : [];

  if (colors.length === 1 && sizes.length === 1) {
    const c = colors[0].toLowerCase();
    const s = sizes[0].toLowerCase();
    if ((c === 'default' || c === 'as image') && (s === 'default' || s === 'one size')) {
      return [];
    }
  }

  if (colors.length === 1 && colors[0].toLowerCase() === 'default') return [];
  return colors;
}

/** Material ribbon on PDP — material string + tag labels (same as ProductPage UI). */
function getMaterialShareParts(product) {
  const material = product?.material != null ? String(product.material).trim() : '';
  const tags = Array.isArray(product?.tags)
    ? product.tags.map(normalizeTagLabel).filter(Boolean)
    : [];
  const parts = [];
  if (material) parts.push(material);
  tags.forEach((t) => {
    if (t && !parts.includes(t)) parts.push(t);
  });
  return parts;
}

/**
 * Multi-line share body (no URL) for WhatsApp, X, native share, copy.
 * Uses *bold* for WhatsApp where supported.
 */
export function buildProductShareText(product) {
  if (!product || typeof product !== 'object') return 'Product';

  const lines = [];
  const name = (product.name && String(product.name).trim()) || 'Product';
  lines.push(`*${name}*`);

  const prices = normalizeProductPricesForDisplay(product);
  const selling = prices.price;
  const list = prices.originalPrice;
  const onSale = prices.isOnSale && list != null && selling != null && list > selling;

  if (selling != null && Number.isFinite(Number(selling))) {
    lines.push(`*Price:* ${formatRs(selling)}`);
  }

  if (onSale && list != null) {
    const pct = Math.round(((list - selling) / list) * 100);
    lines.push(`*Sale:* was ${formatRs(list)}${pct > 0 ? ` (${pct}% OFF)` : ''}`);
  }

  const materialParts = getMaterialShareParts(product);
  if (materialParts.length) lines.push(`*Material:* ${materialParts.join(' / ')}`);

  const colors = getShareColorLabels(product);
  if (colors.length) lines.push(`*Colors:* ${colors.join(', ')}`);

  return lines.join('\n');
}

/** Plain one-line description for OG / Facebook link preview (no WhatsApp markdown). */
export function buildProductShareOgDescription(product) {
  if (!product || typeof product !== 'object') return 'Product';

  const name = (product.name && String(product.name).trim()) || 'Product';
  const prices = normalizeProductPricesForDisplay(product);
  const selling = prices.price;
  const list = prices.originalPrice;
  const onSale = prices.isOnSale && list != null && selling != null && list > selling;

  const parts = [name];
  if (selling != null && Number.isFinite(Number(selling))) {
    parts.push(formatRs(selling));
  }
  if (onSale && list != null) {
    const pct = Math.round(((list - selling) / list) * 100);
    parts.push(`Sale: was ${formatRs(list)}${pct > 0 ? ` (${pct}% OFF)` : ''}`);
  }
  const materialParts = getMaterialShareParts(product);
  if (materialParts.length) parts.push(materialParts.join(' / '));

  return parts.join(' — ');
}

/** Title + text + optional URL for share APIs and deep links. */
export function buildProductShareMeta(product, url = '') {
  const pageUrl = getProductSharePageUrl(product, url);
  const title = (product?.name && String(product.name).trim()) || 'Product';
  const text = buildProductShareText(product);
  const message = pageUrl ? `${text}\n\n${pageUrl}` : text;
  return { url: pageUrl, title, text, message };
}

/** Full share body for clipboard — product details + link on separate lines. */
export function getProductShareClipboardText(product, url = '') {
  return buildProductShareMeta(product, url).message;
}

/** Copy share message (details + URL) with clipboard API + textarea fallback. */
export async function copyProductShareToClipboard(product, url = '') {
  const copyText = getProductShareClipboardText(product, url);
  const ok = await copyTextToClipboard(copyText);
  if (ok) return true;

  if (typeof window !== 'undefined' && typeof window.prompt === 'function') {
    window.prompt('Copy product details:', copyText);
    return true;
  }

  return false;
}
